import { useState, useEffect, useRef } from "react";
import { supabase, BRAND_LIGHT, SCORING_PROMPT, INK, TEXT, MUTED, LINE, SURFACE, text, card as cardTok, btn, btnSm, linkBtn, errorBanner, savedText, segWrap, segBtn, SHADOW_POP, ROW_LINE, FAINT, WHITE, NEUTRAL_BG, HOVER_BG, ARCH_COLORS, ARCH_QUAD_FILLS, BG } from "./constants";
import { evalItemIds, evalCriteria, evalAvg5, evalByVector, BackButton, ConfirmBar, ConfirmDialog, MoreMenu, SelectRow, CandidateAvatar, OptionList } from "./shared";
import CandidateAvatarEditable from "./CandidateAvatarEditable";
import { CandidateFormModal } from "./CandidatesView";
import { useLocalized } from "./lib/translate";
import CVTab from "./CVTab";
import PositionEvaluationTab from "./PositionEvaluationTab";
import RadarChart from "./RadarChart";
import RefAccordion, { refView } from "./RefAccordion";
import AddReferenceInline from "./AddReferenceInline";
import ReferenceManager from "./ReferenceManager";
import { track } from "./lib/analytics";
import { aiFetch, authFetch } from "./lib/ai";
import { useIsMobile, MOBILE_TOP } from "./lib/responsive";
import { Phone, Mail, ExternalLink, ChevronDown } from "lucide-react";

// El salario es texto libre y viene de veinte formatos distintos. Se añade el
// símbolo solo si no trae ninguno ya, y se respeta el que hubiera.
const withCurrency = (raw) => {
  if (!raw) return "";
  const s = String(raw).trim();
  if (!s) return "";
  if (/[€$£¥]|\b(eur|usd|gbp|chf|sek|nok|dkk)\b/i.test(s)) return s;   // ya trae divisa
  if (!/\d/.test(s)) return s;                                        // no es una cifra
  return s + " €";
};

// La nota de referencias viene en % ("88%"): en la ficha, sobre 5 como las demás (31/10)
const refOutOf5 = (pct) => { const n = parseFloat(String(pct).replace(",", ".")); return Number.isFinite(n) ? (n / 20).toFixed(1) : pct; };

// Resumen de la izquierda (03/11): UNA pieza para Cualificación, Evaluación y
// Referencias (en esta, los puntos en común y el veredicto).
// Los 4 vectores sobre 5, puntos fuertes y qué explorar; abajo, las acciones
// de cada pestaña. Fijo al bajar en escritorio; en el móvil, arriba.
const SummaryPanel = ({ es, isMobile, sub, vectors = [], strengths = [], weaknesses = [], strengthsLabel, extra, footer }) => {
  const bulletList = (items) => items.map((x, i) => (
    <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "flex-start" }}>
      <div style={{ width: 5, height: 5, borderRadius: "50%", background: FAINT, marginTop: 7, flexShrink: 0 }} />
      <span style={{ fontSize: 13, color: INK, lineHeight: 1.55 }}>{x}</span>
    </div>
  ));
  const box = { background: WHITE, border: "1.5px solid " + LINE, borderRadius: 14 };
  return (
    <div style={isMobile ? { ...box, padding: "18px 16px", boxSizing: "border-box", minWidth: 0 } : { ...box, padding: "20px 20px 22px", position: "sticky", top: 16, maxHeight: "calc(100vh - 32px)", overflowY: "auto", boxSizing: "border-box" }}>
      <div style={{ marginBottom: 16 }}>
        <div style={text.cardTitle}>{es ? "Resumen" : "Summary"}</div>
        {sub && <div style={{ ...text.cardSub, marginTop: 2 }}>{sub}</div>}
      </div>
      {vectors.map(([label, v]) => (
        <div key={label} style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 5 }}>
            <span style={{ color: TEXT }}>{label}</span>
            <span style={{ color: INK, fontWeight: 700 }}>{v || 0}<span style={{ color: MUTED, fontWeight: 500 }}>/5</span></span>
          </div>
          <div style={{ background: NEUTRAL_BG, borderRadius: 4, height: 5 }}>
            {/* Gris oscuro: la barra no se pulsa (el azul es solo para lo pulsable) */}
            <div style={{ background: TEXT, borderRadius: 4, height: 5, width: `${((v || 0) / 5) * 100}%` }} />
          </div>
        </div>
      ))}
      {strengths.length > 0 && (<>
        <div style={{ ...text.label, marginTop: vectors.length ? 18 : 0, marginBottom: 8, paddingTop: vectors.length ? 14 : 0, borderTop: vectors.length ? `1px solid ${ROW_LINE}` : "none" }}>{strengthsLabel}</div>
        {bulletList(strengths)}
      </>)}
      {weaknesses.length > 0 && (<>
        <div style={{ ...text.label, marginTop: 14, marginBottom: 8 }}>{es ? "A explorar" : "To probe"}</div>
        {bulletList(weaknesses)}
      </>)}
      {extra}
      {footer && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 16, paddingTop: 14, borderTop: `1px solid ${ROW_LINE}` }}>{footer}</div>
      )}
    </div>
  );
};

const CandidateDetailView = ({ t, lang, candidate, position, setView, candidateStatuses = {}, setCandidateStatuses = () => {}, initialTab = null, backView = "candidates", seenReports = new Set(), setSeenReports = () => {}, photoUrls = {}, setPhotoUrls = () => {}, isAdmin = false, onUpdateCandidate = () => {}, onDeleteCandidate = () => {}, calendlyLink = "", companyName = "" }) => {
  // Desde el 03/11, UN nivel de pestañas en el orden del proceso: CV ·
  // Cualificación · Evaluación · Referencias · Comparar. El resumen de la
  // izquierda solo sale en Cualificación (es de ahí). Por dentro:
  // Dos vistas desde el 08/10 (antes seis; ver tag ficha-candidato-v1):
  //   report     = CV + cualificación + referencias en una sola pantalla
  //   evaluation = evaluación del equipo + comparar + guía de entrevista
  // Otras pantallas siguen mandando los nombres antiguos: se traducen aquí.
  const toNewTab = (x) => (x === "comparison" ? "compare" : ["evaluation", "guide"].includes(x) ? "evaluation" : "report");
  // Móvil: una columna, sin fijos, toques de 40 px. El escritorio no cambia.
  const isMobile = useIsMobile();
  const [tab, setTab] = useState(toNewTab(initialTab));
  const tabsAnchorRef = useRef(null);
  // Escritorio (24/10): al bajar y perder de vista las pestañas, una barra fina
  // fija arriba con lo justo (quién, estado, nota), las pestañas y la acción
  const [compactBar, setCompactBar] = useState(false);
  useEffect(() => {
    if (isMobile) { setCompactBar(false); return undefined; }
    const anchor = tabsAnchorRef.current;
    const root = document.querySelector("main");
    if (!anchor || !root || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([e]) => {
      setCompactBar(!e.isIntersecting && e.boundingClientRect.top < (e.rootBounds ? e.rootBounds.top : 0));
    }, { root, threshold: 0 });
    io.observe(anchor);
    return () => io.disconnect();
  }, [isMobile]);
  // Subpestañas: cada bloque a ancho completo, como estaba diseñado
  const [reportSub, setReportSub] = useState(initialTab === "references" ? "refs" : initialTab === "cv" ? "cv" : "qual");

  // Mark report as seen when References tab is viewed
  const handleSetTab = (picked) => {
    // Las pestañas de la ficha (31/10): qual / refs / cv son la vista "report"
    const newTab = ["qual", "refs", "cv"].includes(picked) ? "report" : picked;
    if (newTab === "report") setReportSub(picked);
    setTab(newTab);
    if (newTab === "evaluation") track("evaluation_tab_opened", { position: position?.title || "" });
    if (newTab === "report" && candidate) {
      setSeenReports(prev => new Set([...prev, candidate.id]));
    }
    // Al cambiar de pestaña ya no se sube del todo (24/10): si se había bajado
    // más allá de las pestañas, se coloca justo en ellas (con el principio del
    // contenido nuevo a la vista); si no, no se mueve.
    const mainEl = document.querySelector("main");
    const anchor = tabsAnchorRef.current;
    if (mainEl && anchor) {
      const anchorTop = anchor.getBoundingClientRect().top - mainEl.getBoundingClientRect().top + mainEl.scrollTop - (isMobile ? MOBILE_TOP : 0);
      if (mainEl.scrollTop > anchorTop) mainEl.scrollTop = Math.max(0, anchorTop);
    }
  };

  // Also mark on mount if opening directly on references tab
  useEffect(() => {
    if (toNewTab(initialTab) === "report" && candidate) {
      setSeenReports(prev => new Set([...prev, candidate.id]));
    }
  }, [candidate?.id]);
  const [editingQuestion, setEditingQuestion] = useState(null); // { si, ii }
  // Móvil: la respuesta se lee como texto y solo pasa a campo (16 px, para que
  // el iPhone no amplíe) al tocarla
  const [editingAnswer, setEditingAnswer] = useState(null); // { si, ii }
  const [compareId, setCompareId] = useState("");
  const [compareOpen, setCompareOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(false);
  const [contactPhone, setContactPhone] = useState(candidate.phone || "");
  const [contactEmail, setContactEmail] = useState(candidate.email || "");
  const [contactLinkedin, setContactLinkedin] = useState(candidate.linkedin || "");
  const [contactSaving, setContactSaving] = useState(false);
  const [compareTab, setCompareTab] = useState("qualification");
  // Fuente de datos del bloque de comparación: el scoring de Rutment o la
  // evaluación de la empresa. Antes eran dos subpestañas casi idénticas.
  const [compareSource, setCompareSource] = useState("qualification");

  // Live evaluation scores — reactive, shared between Evaluation tab and header metric
  // Criteria: c1a,c1b (technical), c2a,c2b (soft), c3a,c3b (cultural), c4a,c4b (growth)
  const evalDataForCandidate = {
    scores: candidate.evalScores || {},
    managers: candidate.evalManagers || [],
    notes: candidate.evalNotes || {},
  };

  const [liveScores, setLiveScores] = useState(evalDataForCandidate.scores);
  const [liveManagers, setLiveManagers] = useState(evalDataForCandidate.managers);
  const [liveNotes, setLiveNotes] = useState(evalDataForCandidate.notes);
  const [evalInsightsLoading, setEvalInsightsLoading] = useState(false);

  // Load eval data from Supabase for real (non-mock) candidates
  useEffect(() => {
    if (!candidate) return;
    supabase.from("candidates").select("eval_managers, eval_scores, eval_notes, eval_ai_insights").eq("id", candidate.id).single()
      .then(({ data }) => {
        if (!data) return;
        setLiveManagers(data.eval_managers || []);
        setLiveScores(data.eval_scores || {});
        setLiveNotes(data.eval_notes || {});
        if (data.eval_ai_insights) setLiveAiInsights(data.eval_ai_insights);
      });
  }, [candidate.id]);

  const [liveAiInsights, setLiveAiInsights] = useState(candidate.evalAiInsights || null);
  const [qualLinkCopied, setQualLinkCopied] = useState(false);
  const [qualLinkLoading, setQualLinkLoading] = useState(false);
  const [reportInsightsLoading, setReportInsightsLoading] = useState(false);

  const handleSendQualForm = async () => {
    setQualLinkLoading(true);
    try {
      const { data: existing } = await supabase
        .from("qual_tokens")
        .select("token")
        .eq("candidate_id", String(candidate.id))
        .eq("position_id", String(position?.id || ""))
        .is("completed_at", null)
        .maybeSingle();

      let token;
      if (existing?.token) {
        token = existing.token;
      } else {
        const { data: created } = await supabase
          .from("qual_tokens")
          .insert({ candidate_id: String(candidate.id), position_id: String(position?.id || "") })
          .select("token")
          .single();
        token = created?.token;
      }

      if (token) {
        const link = `https://www.rutment.com?qual=${token}`;
        await navigator.clipboard.writeText(link);
        setQualLinkCopied(true);
        setTimeout(() => setQualLinkCopied(false), 3000);
      }
    } catch(e) { console.error("Qual link error:", e); }
    setQualLinkLoading(false);
  };
  const generateReportInsights = async () => {
    const qual=(candidate.qualSections||[]).map(s=>s.section+": "+(s.items||[]).map(i=>i.q+" > "+i.a).join(" | ")).join("\n");
    const jd=position?.jobDescription||position?.title||""; if(!qual)return;
    let cultureDoc="";
    if(position?.clientId){const{data:cd}=await supabase.from("culture_docs").select("generated_doc").eq("client_id",position.clientId).maybeSingle();if(cd?.generated_doc)cultureDoc=cd.generated_doc;}
    const formData=position?.formData||{};
    const positionContext=[
      "JOB DESCRIPTION:\n"+jd,
      formData.hardSkills?"HARD SKILLS REQUIRED:\n"+formData.hardSkills:"",
      formData.softSkills?"SOFT SKILLS REQUIRED:\n"+formData.softSkills:"",
      formData.culture?"CULTURE FIT REQUIRED:\n"+formData.culture:"",
      cultureDoc?"COMPANY CULTURE DOCUMENT:\n"+cultureDoc:""
    ].filter(Boolean).join("\n\n");
    setReportInsightsLoading(true);
    try{const res=await aiFetch({method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({max_tokens:1000,system:SCORING_PROMPT+(lang==="es"?"\n\nWrite strengths, weaknesses and summary in Spanish (Spain).":"\n\nWrite strengths, weaknesses and summary in English."),messages:[{role:"user",content:"POSITION CONTEXT:\n"+positionContext+"\n\nCANDIDATE RESPONSES:\n"+qual+"\n\nScore the candidate considering ALL aspects of the position context above (job requirements, hard skills, soft skills, and company culture), not just the job description. Pay special attention to cultural fit when scoring the cultural dimension."}]})});
    const data=await res.json();const text=data.content?.map(b=>b.text||"").join("")||"{}";
    let parsed={};try{parsed=JSON.parse(text.replace(/```json|```/g,"").trim());}catch(e){}
    const updates={scoring:parsed.scoring||candidate.scoring,strengths:parsed.strengths||candidate.strengths,weaknesses:parsed.weaknesses||candidate.weaknesses};
    if(await saveCandidate({scoring:updates.scoring,strengths:updates.strengths,weaknesses:updates.weaknesses}))onUpdateCandidate({...candidate,...updates});track("ai_scoring_run",{position:position?.title||""});}catch(err){console.error(err);}
    setReportInsightsLoading(false);
  };
  // Resumen de las entrevistas (03/11): la IA lee las notas de cada pregunta y
  // los comentarios de cada entrevistador y devuelve en qué coinciden como
  // fuerte y qué queda por verificar. Se guarda en eval_ai_insights (lo usa
  // también Comparar) y solo se rehace al pulsar.
  const generateEvalInsights = async () => {
    const es = lang === "es";
    const crit = evalCriteria(lang, position?.evalQuestions);
    const lines = (liveManagers || []).map(m => {
      const sc = (liveScores || {})[m.id] || {};
      const items = crit.flatMap(sec => sec.items.map(it => `${sec.name} · ${it.label}: ${sc[it.id] ? sc[it.id] + "/3" : "—"}`));
      const note = (liveNotes || {})[m.id];
      return `Interviewer: ${m.name || "—"}\n${items.join("\n")}${note ? "\nComments: " + note : ""}`;
    }).join("\n\n");
    if (!lines) return;
    setEvalInsightsLoading(true);
    try {
      const res = await aiFetch({ body: JSON.stringify({ max_tokens: 700,
        system: `You are a senior executive recruiter summarising the client team's interview evaluations of one candidate. Each question is scored 1-3 (3 = strong, empty = not scored). Return ONLY a JSON object with: "strengths" (array of 2-4 short strings: what the interviewers agree is strong, grounded in their scores or comments) and "weaknesses" (array of 1-3 short strings: where scores are low or interviewers disagree, phrased as what to probe or verify next). Never invent facts beyond the data. ${es ? "Write in Spanish (Spain)." : "Write in English."} No markdown.`,
        messages: [{ role: "user", content: `Position: ${position?.title || ""}\nCandidate: ${candidate.name}\n\n${lines}` }] }) });
      const data = await res.json();
      const parsed = JSON.parse((data.content?.[0]?.text || "{}").replace(/```json|```/g, "").trim());
      const next = { strengths: parsed.strengths || [], weaknesses: parsed.weaknesses || [] };
      if (await saveCandidate({ eval_ai_insights: next })) { setLiveAiInsights(next); onUpdateCandidate({ ...candidate, evalAiInsights: next }); }
    } catch (e) { console.error(e); }
    setEvalInsightsLoading(false);
  };
  const [liveRefInsights, setLiveRefInsights] = useState(candidate.refInsights || null);
  const [liveRefInsightsLoading, setLiveRefInsightsLoading] = useState(false);

  // Media /5 de la evaluación del equipo, sobre las preguntas de la posición
  // y solo las puntuadas (helpers comunes de shared.jsx).
  const evalIds = evalItemIds(lang, position?.evalQuestions);
  const computeLiveEvalAvg = (scores) => evalAvg5(Object.keys(scores || {}).map(id => ({ id })), scores, evalIds);
  const liveEvalAvg = computeLiveEvalAvg(liveScores);
  const [actionStep, setActionStep] = useState(null); // null | "validate" | "email"
  // Fase del Proceso pendiente de confirmar: "Seleccionado" y "No
  // seleccionado" las ve el candidato al momento en su portal (23/10)
  const [pendingProcess, setPendingProcess] = useState(null);
  // Editar datos y eliminar la candidatura, solo admin (23/10). Antes solo se
  // podía desde la Tabla, que está congelada: nombre, empresa, salario o un
  // duplicado no tenían arreglo desde la interfaz.
  const [editingData, setEditingData] = useState(false);
  const [askDelete, setAskDelete] = useState(false);
  // Menú ⋯ de la cabecera en móvil (Editar datos, Editar contacto, Eliminar)
  const deleteThis = async () => {
    setAskDelete(false);
    const { data, error } = await supabase.from("candidates").delete().eq("id", String(candidate.id)).select("id");
    if (error || !data || data.length === 0) {
      setSaveMsg(lang === "es" ? `No se pudo eliminar a ${candidate.name}.` : `Couldn't delete ${candidate.name}.`);
      return;
    }
    onDeleteCandidate(candidate);
    setView(backView);
  };
  const saveProcess = async (ns) => {
    setPendingProcess(null);
    if (await saveCandidate({ process_status: ns })) onUpdateCandidate({ ...candidate, processStatus: ns, process_status: ns });
  };
  // RLS bloqueado devuelve 200 con 0 filas, sin error: cada guardado de la
  // ficha comprueba que volvió la fila y, si no, lo dice en pantalla.
  const [saveMsg, setSaveMsg] = useState("");
  const saveCandidate = async (fields) => {
    const { data, error } = await supabase.from("candidates").update(fields).eq("id", String(candidate.id)).select("id");
    const ok = !error && !!data && data.length > 0;
    setSaveMsg(ok ? "" : (lang === "es" ? "No se pudo guardar el cambio. Inténtalo de nuevo." : "Couldn't save the change. Please try again.") + (error ? " " + error.message : ""));
    return ok;
  };

  const currentStatus = candidateStatuses[candidate.id] || candidate.status;
  const isPending = currentStatus === "pending";

  const candidateEmail = candidate.email || "";

  const sendValidationEmail = async () => {
    try {
      const res = await authFetch("/api/send-email", {
        body: JSON.stringify({
          type: "candidate_validated",
          candidateId: candidate.id,
          candidate: { name: candidate.name, email: candidateEmail, position: candidate.position, company: candidate.company, linkedin: candidate.linkedin },
          position: { title: position?.title || "" },
          interviewers: [],
          calendlyLink: calendlyLink,
          lang,
        })
      });
      // Decir si el correo salió (23/10): antes se daba por enviado siempre
      const out = await res.json().catch(() => ({}));
      if (!res.ok) setSaveMsg(lang === "es" ? `No se pudo enviar el correo a ${candidate.name}.` : `The email to ${candidate.name} could not be sent.`);
      else if (out.candidateEmailed === false) setSaveMsg(lang === "es" ? `${candidate.name} no tiene email en su ficha: no se le ha enviado nada.` : `${candidate.name} has no email on file: nothing was sent to them.`);
    } catch(e) { console.error("Email send error:", e); setSaveMsg(lang === "es" ? "No se pudo enviar el correo." : "The email could not be sent."); }
  };

  const handleConfirmValidate = async (sendEmail) => {
    const before = candidateStatuses[candidate.id];
    setCandidateStatuses(prev => ({ ...prev, [candidate.id]: "approved" }));
    setActionStep(null);
    // Si no se guardó, se deshace y no se manda el correo.
    if (!(await saveCandidate({ status: "approved" }))) {
      setCandidateStatuses(prev => ({ ...prev, [candidate.id]: before }));
      return;
    }
    track("candidate_validated", { decision: "approved", email_sent: !!sendEmail, position: position?.title || "" });
    if (sendEmail) await sendValidationEmail();
  };

  const handleScheduleInterview = async () => {
    track("interview_scheduled", { position: position?.title || "" });
    setActionStep(null);
    await sendValidationEmail();
  };

  // Request references (client side)
  const [refRequested, setRefRequested] = useState(false);
  const [refRequesting, setRefRequesting] = useState(false);
  const hasEval = !!(candidate.evalManagers && candidate.evalManagers.length > 0 && candidate.evalUpdatedAt);

  const handleRequestRefs = async () => {
    if (refRequesting || refRequested) return;
    setRefRequesting(true);
    try {
      await authFetch("/api/send-email", {
        body: JSON.stringify({
          type: "ref_report_requested",
          candidateId: candidate.id,
          candidate: { name: candidate.name, position: candidate.position, company: candidate.company },
          position: { title: position?.title || "" },
          lang,
        })
      });
      await supabase.from("admin_notifications").insert({
        type: "ref_report_requested",
        message: `Reference report requested for ${candidate.name} (${position?.title || ""})`,
        candidate_name: candidate.name,
        position_title: position?.title || "",
      });
      setRefRequested(true);
      track("reference_questionnaire_sent", { position: position?.title || "" });
    } catch (e) { console.error("Request refs error:", e); }
    setRefRequesting(false);
  };
  // Móvil: nombres cortos para que las cinco quepan sin desplazar (31/10)
  const nRefs = candidate.refsData && candidate.refsData.length ? ` (${candidate.refsData.length})` : "";
  // 03/11: en el orden del proceso (CV, Cualificación, Evaluación, Referencias)
  // y Comparar al final. Sin evaluaciones o sin referencias todavía, su
  // pestaña va en gris claro (se puede abrir igual).
  const noEvalYet = liveEvalAvg === null && !hasEval;
  const noRefsYet = !(candidate.refsData && candidate.refsData.length);
  const tabs = [
    { id: "cv", label: "CV" },
    { id: "qual", label: isMobile ? (lang === "es" ? "Cualif." : "Qualif.") : (lang === "es" ? "Cualificación" : "Qualification") },
    { id: "evaluation", label: isMobile ? (lang === "es" ? "Evaluac." : "Eval.") : (lang === "es" ? "Evaluación" : "Evaluation"), empty: noEvalYet },
    { id: "refs", label: (isMobile ? "Refs" : (lang === "es" ? "Referencias" : "References")) + nRefs, empty: noRefsYet },
    { id: "compare", label: lang === "es" ? "Comparar" : "Compare" },
  ];
  // Pestaña marcada: dentro del informe, la subvista
  const activeTab = tab === "report" ? reportSub : tab;
  const emptyTab = (tab_) => (tab_.empty && activeTab !== tab_.id ? { color: FAINT } : null);

  const otherCandidates = (position?.candidates || []).filter(c => c.id !== candidate.id);
  const allCandidates = position?.candidates || [];

  // Compute average scoring across all candidates
  const avgScoring = {
    technical: parseFloat((allCandidates.reduce((s, c) => s + c.scoring.technical, 0) / allCandidates.length).toFixed(1)),
    soft:      parseFloat((allCandidates.reduce((s, c) => s + c.scoring.soft,      0) / allCandidates.length).toFixed(1)),
    cultural:  parseFloat((allCandidates.reduce((s, c) => s + c.scoring.cultural,  0) / allCandidates.length).toFixed(1)),
    growth:    parseFloat((allCandidates.reduce((s, c) => s + c.scoring.growth,    0) / allCandidates.length).toFixed(1)),
  };
  const avgCandidate = { name: lang === "es" ? "Media candidatos" : "Candidate avg.", scoring: avgScoring, strengths: [], weaknesses: [] };

  const isAverage = compareId === "average";
  const compareOptions = [["", lang === "es" ? "Nadie" : "Nobody"], ["average", t.candidateAverage], ...otherCandidates.map(c => [String(c.id), c.name + (c.company ? " · " + c.company : "")])];
  const compareCandidate = isAverage ? avgCandidate : (otherCandidates.find(c => String(c.id) === String(compareId)) || null);
  const radarLabels = [t.technicalFit, t.softSkills, t.culturalFit, t.growthPotential];
  const radarTooltips = lang === "es" ? [
    "Indica si el candidato ha desempeñado este rol anteriormente y si dispone de los conocimientos necesarios sobre el puesto, el sector, el mercado, la red de contactos, etc.",
    "Indica si el candidato posee las habilidades interpersonales requeridas para esta posición teniendo en cuenta su nivel de seniority.",
    "Mide el encaje del candidato con el equipo, la dinámica y la situación actual de la empresa.",
    "Mide la capacidad del candidato de adaptarse con éxito a distintos roles, empresas y equipos, asumiendo nuevas responsabilidades a lo largo de su carrera.",
  ] : [
    "Indicates whether the candidate has performed this role before and has all the knowledge required for the position — including sector expertise, market understanding, and relevant network.",
    "Indicates whether the candidate has the interpersonal and behavioural skills required for this position at their level of seniority.",
    "Measures how well the candidate's values, working style and personality align with the company's team dynamics and current situation.",
    "Measures the candidate's ability to successfully adapt to different roles, companies and teams, taking on new responsibilities and growing throughout their career.",
  ];

  const avgScore = (Object.values(candidate.scoring).reduce((a, b) => a + b, 0) / 4).toFixed(1);

  // Company evaluation scores (from Evaluation tab inputs, converted to /5)
  // Compute company eval dynamically from Supabase eval_scores
  const computeEvalFromScores = (evalScores, evalManagers) => {
    if (!evalScores || !evalManagers || evalManagers.length === 0) return null;
    const dims = evalByVector(evalManagers, evalScores, evalIds);
    dims.avg = parseFloat((Object.values(dims).reduce((a,b)=>a+b,0)/4).toFixed(1));
    return dims;
  };
  const candidateEval = computeEvalFromScores(liveScores, liveManagers);

  // Reference coinciding % per candidate

  // Textos de la IA en el idioma de la pantalla (lib/translate.js): se guardan
  // en el idioma en que se generaron y antes salían mezclados (23/10)
  const displayStrengths = useLocalized(candidate.strengths, lang);
  const displayWeaknesses = useLocalized(candidate.weaknesses, lang);
  const evalStrengthsL = useLocalized(liveAiInsights?.strengths, lang);
  const evalWeaknessesL = useLocalized(liveAiInsights?.weaknesses, lang);
  const refPointsL = useLocalized([...(liveRefInsights?.points || []), liveRefInsights?.verdict || ""], lang);
  const cmpStrengthsL = useLocalized(compareCandidate && !isAverage ? compareCandidate.strengths : [], lang);
  const cmpWeaknessesL = useLocalized(compareCandidate && !isAverage ? compareCandidate.weaknesses : [], lang);
  const cmpEvalStrengthsL = useLocalized(compareCandidate && !isAverage ? compareCandidate.evalAiInsights?.strengths : [], lang);
  const cmpEvalWeaknessesL = useLocalized(compareCandidate && !isAverage ? compareCandidate.evalAiInsights?.weaknesses : [], lang);
  // Cualificación (31/10): los títulos de sección y las preguntas los genera la
  // IA en el idioma de la posición; se pintan en el de la pantalla, como el
  // Resumen de al lado. Las respuestas las escribe el candidato: no se tocan.
  const qualSecs = candidate.qualSections || [];
  const qualTexts = qualSecs.flatMap(s => [s.section || "", ...(s.items || []).map(it => it.q || "")]);
  const qualTextsL = useLocalized(qualTexts, lang);
  const qualL = (si, ii) => {
    let k = 0;
    for (let i = 0; i < si; i++) k += 1 + (qualSecs[i].items || []).length;
    const v = qualTextsL[ii == null ? k : k + 1 + ii];
    return v != null && v !== "" ? v : (ii == null ? qualSecs[si]?.section : qualSecs[si]?.items?.[ii]?.q);
  };

  // Botones de decisión de la cabecera. En escritorio van bajo el nombre; en
  // móvil, al pie de la tarjeta y a ancho completo (toques de 40 px).

  // Campos en móvil: 44 de alto y 16 px (iOS amplía por debajo de 16)
  const inputM = isMobile ? { width: "100%", height: 44, fontSize: 16, padding: "0 12px", borderRadius: 10, boxSizing: "border-box", fontFamily: "inherit" } : null;

  // Estado tal como se enseña (mismo cálculo que la caja de escritorio)
  const shownStatus = (() => {
    if (currentStatus === "approved") return candidate.references >= 2 ? "references" : liveEvalAvg !== null ? "interview" : "validated";
    return currentStatus;
  })();
  const statusLabel = ({
    pending: t.pending, validated: lang === "es" ? "Validado" : "Validated", approved: lang === "es" ? "Validado" : "Validated",
    interview: lang === "es" ? "Entrevista" : "Interview", references: lang === "es" ? "Referencias" : "References", rejected: t.rejected,
  })[shownStatus] || t.pending;
  const PROCESS_OPTS = [
    ["receiving_applications", lang === "es" ? "Recibiendo candidaturas" : "Receiving applications"],
    ["interview_process", lang === "es" ? "Entrevistas" : "Interview process"],
    ["final_round", lang === "es" ? "Ronda final" : "Final round"],
    ["selected", lang === "es" ? "Seleccionado" : "Selected"],
    ["not_selected", lang === "es" ? "No seleccionado" : "Not selected"],
  ];
  const processValue = candidate.processStatus || candidate.process_status || "receiving_applications";
  const changeStatus = async (ns) => {
    const before = currentStatus;
    setCandidateStatuses(prev => ({ ...prev, [candidate.id]: ns }));
    if (!(await saveCandidate({ status: ns }))) setCandidateStatuses(prev => ({ ...prev, [candidate.id]: before }));
  };
  const changeProcess = (ns) => {
    if (ns === "selected" || ns === "not_selected") setPendingProcess(ns);
    else saveProcess(ns);
  };
  const processConfirm = pendingProcess && (
    <ConfirmDialog
      danger={pendingProcess === "not_selected"}
      title={lang === "es" ? "Publicar la fase al candidato" : "Publish the stage to the candidate"}
      message={lang === "es"
        ? <><strong>{candidate.name}</strong> verá «{pendingProcess === "selected" ? "Seleccionado" : "No seleccionado"}» en su portal en cuanto lo guardes.</>
        : <><strong>{candidate.name}</strong> will see “{pendingProcess === "selected" ? "Selected" : "Not selected"}” in their portal as soon as you save it.</>}
      actionLabel={lang === "es" ? "Publicar" : "Publish"}
      cancelLabel={lang === "es" ? "Cancelar" : "Cancel"}
      onConfirm={() => saveProcess(pendingProcess)}
      onCancel={() => setPendingProcess(null)} />
  );

  // ── CABECERA (móvil 24/10; escritorio con el mismo criterio, 24/10) ─────
  // Una sola tarjeta que se lee de arriba abajo: quién es, cómo contactarle
  // (botones que llaman, escriben o abren LinkedIn), las dos fases como filas
  // de lista (toda la fila abre el selector), las puntuaciones en una franja y
  // UNA acción principal. Editar y eliminar van al menú ⋯. En escritorio la
  // acción va a la derecha del nombre, Rechazar pasa al ⋯, y fases y
  // puntuaciones comparten una franja.
  const contactBtns = [
    candidate.phone && { href: "tel:" + String(candidate.phone).replace(/\s+/g, ""), label: lang === "es" ? "Llamar" : "Call", Icon: Phone },
    candidate.email && { href: "mailto:" + candidate.email, label: "Email", Icon: Mail },
    candidate.linkedin && { href: candidate.linkedin, label: "LinkedIn", Icon: ExternalLink, ext: true },
  ].filter(Boolean);
  const scoreCells = [
    { v: avgScore, of: "/5", l: lang === "es" ? "Cualific." : "Qualif." },
    liveEvalAvg !== null && { v: Number(liveEvalAvg).toFixed(1), of: "/5", l: lang === "es" ? "Entrevistas" : "Interviews" },
    liveRefInsights && candidate.refsData && candidate.refsData.length > 0 && { v: refOutOf5(liveRefInsights.pct), of: "/5", l: lang === "es" ? "Referencias" : "References" },
  ].filter(Boolean);
  const canAct = actionStep === null && currentStatus !== "rejected";
  const menuItems = [
    !isMobile && canAct && isPending && { label: t.reject, onClick: () => setActionStep("confirmReject") },
    isAdmin && { label: lang === "es" ? "Editar datos" : "Edit details", onClick: () => setEditingData(true) },
    isAdmin && { label: lang === "es" ? "Editar contacto" : "Edit contact", onClick: () => setEditingContact(true) },
    isAdmin && { label: lang === "es" ? "Eliminar candidatura" : "Delete candidacy", onClick: () => setAskDelete(true), danger: true },
  ].filter(Boolean);
  const statusRow = (cell, first) => (
    <SelectRow cell={cell} first={first} label={lang === "es" ? "Estado" : "Status"} display={statusLabel} value={currentStatus}
      options={isAdmin ? [["pending", lang === "es" ? "Pendiente" : "Pending"], ["approved", lang === "es" ? "Validado" : "Validated"], ["interview", lang === "es" ? "Entrevista" : "Interview"], ["references", lang === "es" ? "Referencias" : "References"], ["rejected", lang === "es" ? "Rechazado" : "Rejected"]] : null}
      onChange={isAdmin ? changeStatus : null} />
  );
  const processRow = (cell) => isAdmin && (
    <SelectRow cell={cell} label={lang === "es" ? "Fase que ve el candidato" : "Stage the candidate sees"} display={(PROCESS_OPTS.find(([v]) => v === processValue) || PROCESS_OPTS[0])[1]}
      value={processValue} options={PROCESS_OPTS} onChange={changeProcess} />
  );
  const header = (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: isMobile ? 8 : 12, minHeight: 40 }}>
        <BackButton onClick={() => setView(backView)} label={t.back} ariaLabel={t.back} />
        <MoreMenu label={lang === "es" ? "Más acciones" : "More actions"} items={menuItems} />
      </div>
      <div style={{ ...cardTok, padding: 0, marginBottom: isMobile ? 12 : 20, overflow: isMobile ? "hidden" : "visible" }}>
        <div style={{ display: "flex", gap: isMobile ? 14 : 18, alignItems: "center", padding: isMobile ? 16 : "20px 22px" }}>
          <CandidateAvatarEditable lang={lang} candidateId={candidate.id} name={candidate.name} photoUrls={photoUrls} setPhotoUrls={setPhotoUrls} isAdmin={isAdmin} size={isMobile ? 56 : 72} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2 style={{ fontSize: isMobile ? 22 : 22, fontWeight: 800, color: INK, margin: 0, lineHeight: 1.2, overflowWrap: "anywhere" }}>{candidate.name}</h2>
            <div style={{ ...text.body, marginTop: 2 }}>{[candidate.position, candidate.company].filter(Boolean).join(" · ")}</div>
            <div style={{ ...text.meta, fontSize: 12, marginTop: 2 }}>{[candidate.city, candidate.salary && withCurrency(candidate.salary), candidate.notice].filter(Boolean).join(" · ")}</div>
          </div>
          {!isMobile && (candidate.phone || candidate.email || candidate.linkedin) && (
            <div style={{ textAlign: "right", fontSize: 12, color: MUTED, lineHeight: 1.8, minWidth: 0 }}>
              {candidate.phone && <div>{candidate.phone}</div>}
              <div>
                {candidate.email && <a href={"mailto:" + candidate.email} style={{ color: MUTED, textDecoration: "none" }}>{candidate.email}</a>}
                {candidate.email && candidate.linkedin && " · "}
                {candidate.linkedin && <a href={candidate.linkedin} target="_blank" rel="noopener noreferrer" style={{ ...linkBtn, fontSize: 12 }}>LinkedIn</a>}
              </div>
            </div>
          )}
          {!isMobile && canAct && (
            <button onClick={() => setActionStep(isPending ? "confirmValidate" : "scheduleInterview")} style={{ ...btn.primary, whiteSpace: "nowrap", flexShrink: 0, marginLeft: 8 }}>
              {isPending ? t.validate : (lang === "es" ? "Agendar entrevista" : "Schedule interview")}
            </button>
          )}
        </div>
        {isMobile && contactBtns.length > 0 && (
          <div style={{ display: "flex", gap: 8, padding: "0 16px 14px" }}>
            {contactBtns.map(({ href, label, Icon, ext }) => (
              <a key={href} href={href} {...(ext ? { target: "_blank", rel: "noopener noreferrer" } : null)}
                style={{ ...btn.ghost, flex: "1 1 0", minWidth: 0, height: 40, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, textDecoration: "none", boxSizing: "border-box" }}>
                <Icon size={16} strokeWidth={1.75} />{label}
              </a>
            ))}
          </div>
        )}
        {editingContact && isAdmin && (
          <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 8, padding: isMobile ? "0 16px 14px" : "0 22px 16px" }}>
            <input placeholder={lang === "es" ? "Teléfono" : "Phone"} value={contactPhone} onChange={e => setContactPhone(e.target.value)} style={{ border: "1.5px solid " + LINE, ...inputM }} />
            <input placeholder="Email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} style={{ border: "1.5px solid " + LINE, ...inputM }} />
            <input placeholder={lang === "es" ? "URL de LinkedIn" : "LinkedIn URL"} value={contactLinkedin} onChange={e => setContactLinkedin(e.target.value)} style={{ border: "1.5px solid " + LINE, ...inputM }} />
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setEditingContact(false)} style={{ ...btn.ghost, flex: 1, height: 44 }}>{lang === "es" ? "Cancelar" : "Cancel"}</button>
              <button onClick={async () => {
                setContactSaving(true);
                if (await saveCandidate({ phone: contactPhone, email: contactEmail, linkedin: contactLinkedin })) {
                  onUpdateCandidate({ ...candidate, phone: contactPhone, email: contactEmail, linkedin: contactLinkedin });
                  setEditingContact(false);
                }
                setContactSaving(false);
              }} style={{ ...btn.primary, flex: 1, height: 44 }}>{contactSaving ? "..." : (lang === "es" ? "Guardar" : "Save")}</button>
            </div>
          </div>
        )}
        {isMobile && statusRow(false, false)}
        {isMobile && processRow(false)}
        <div style={{ display: "flex", borderTop: `1px solid ${ROW_LINE}` }}>
          {!isMobile && statusRow(true, true)}
          {!isMobile && processRow(true)}
          {scoreCells.map((c, i) => (
            <div key={i} style={{ flex: 1, textAlign: isMobile ? "center" : "left", padding: isMobile ? "12px 4px" : "12px 20px", borderLeft: i || !isMobile ? `1px solid ${ROW_LINE}` : "none" }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: INK }}>{c.v}{c.of && <span style={{ fontSize: 11, color: FAINT, fontWeight: 600 }}>{c.of}</span>}</div>
              <div style={{ ...text.label, marginTop: 2 }}>{c.l}</div>
            </div>
          ))}
        </div>
        {isMobile && canAct && (
          <div style={{ display: "flex", gap: 8, padding: "12px 16px 16px", borderTop: `1px solid ${ROW_LINE}` }}>
            {isPending ? (<>
              <button onClick={() => setActionStep("confirmValidate")} style={{ ...btn.primary, flex: 2, height: 44 }}>{t.validate}</button>
              <button onClick={() => setActionStep("confirmReject")} style={{ ...btn.ghost, flex: 1, height: 44 }}>{t.reject}</button>
            </>) : (
              <button onClick={() => setActionStep("scheduleInterview")} style={{ ...btn.primary, flex: 1, height: 44 }}>{lang === "es" ? "Agendar entrevista" : "Schedule interview"}</button>
            )}
          </div>
        )}
      </div>
      {processConfirm}
    </>
  );


  return (
    <div style={{ padding: isMobile ? 16 : 32, boxSizing: "border-box", width: "100%", minWidth: isMobile ? 0 : 900 }}>
      {/* Barra fina fija (escritorio): envoltorio de alto 0 pegado arriba, así no
          ocupa sitio; aparece al bajar y se va al volver arriba */}
      {!isMobile && (
        <div style={{ position: "sticky", top: 0, height: 0, zIndex: 60 }}>
          <div aria-hidden={!compactBar} style={{ position: "absolute", top: 0, left: -32, right: -32, display: "flex", alignItems: "center", gap: 14, padding: "10px 32px", background: `${BG}F5`, backdropFilter: "blur(6px)", borderBottom: "1px solid " + LINE, boxShadow: SHADOW_POP,
            opacity: compactBar ? 1 : 0, transform: compactBar ? "translateY(0)" : "translateY(-8px)", pointerEvents: compactBar ? "auto" : "none", transition: "opacity 0.15s, transform 0.15s" }}>
            {photoUrls[candidate.id]
              ? <img src={photoUrls[candidate.id]} alt="" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", border: `1.5px solid ${LINE}`, flexShrink: 0 }} />
              : <CandidateAvatar name={candidate.name} size={36} />}
            <div style={{ minWidth: 0, flexShrink: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: INK, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{candidate.name}</div>
              <div style={{ ...text.meta, fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {[candidate.position, statusLabel, `${lang === "es" ? "Cualif." : "Qualif."} ${avgScore}`].filter(Boolean).join(" · ")}
              </div>
            </div>
            <div style={{ ...segWrap, display: "inline-flex", marginLeft: 12, flexShrink: 0 }}>
              {tabs.map(tab_ => (
                <button key={tab_.id} tabIndex={compactBar ? 0 : -1} onClick={() => handleSetTab(tab_.id)} style={{ ...segBtn(activeTab === tab_.id), padding: "0 16px", height: 30, ...emptyTab(tab_) }}>{tab_.label}</button>
              ))}
            </div>
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
              {canAct && (
                <button tabIndex={compactBar ? 0 : -1} onClick={() => { setActionStep(isPending ? "confirmValidate" : "scheduleInterview"); document.querySelector("main")?.scrollTo({ top: 0, behavior: "smooth" }); }}
                  style={{ ...btn.primary, ...btnSm, whiteSpace: "nowrap" }}>
                  {isPending ? t.validate : (lang === "es" ? "Agendar entrevista" : "Schedule interview")}
                </button>
              )}
              <MoreMenu label={lang === "es" ? "Más acciones" : "More actions"} items={menuItems} size={34} />
            </div>
          </div>
        </div>
      )}
      {header}
      {editingData && (
        <CandidateFormModal position={position} lang={lang} existing={candidate} isAdmin={isAdmin}
          onSave={(upd) => {
            onUpdateCandidate(upd);
            if (upd.status !== candidate.status) setCandidateStatuses(prev => ({ ...prev, [upd.id]: upd.status }));
            setEditingData(false);
          }}
          onClose={() => setEditingData(false)} />
      )}
      {askDelete && (
        <ConfirmDialog title={lang === "es" ? "Eliminar candidatura" : "Delete candidacy"}
          message={lang === "es"
            ? <>¿Eliminar a <strong>{candidate.name}</strong> de esta posición? Se borran su cualificación, evaluaciones y referencias de este proceso, y no se puede deshacer. Su contacto y su persona en Database se conservan.</>
            : <>Delete <strong>{candidate.name}</strong> from this position? Their qualification, evaluations and references for this process go with it, and it can't be undone. Their contact and person in Database are kept.</>}
          actionLabel={lang === "es" ? "Eliminar" : "Delete"} cancelLabel={lang === "es" ? "Cancelar" : "Cancel"}
          onConfirm={deleteThis} onCancel={() => setAskDelete(false)} />
      )}

      {/* Confirm banners — validate / reject / schedule */}
      {saveMsg && (
        <div style={{ ...errorBanner, marginBottom: 16 }}>{saveMsg}</div>
      )}
      {/* Validar tiene tres opciones: solo "Confirmar y enviar email" va en
          color; "sin email" y Cancelar, en gris. */}
      {actionStep === "confirmValidate" && (
        <ConfirmBar danger={false} style={{ marginBottom: 16 }}
          message={lang === "es" ? <>¿Validar a <strong>{candidate.name}</strong>?</> : <>Validate <strong>{candidate.name}</strong>?</>}
          actionLabel={<>✉ {lang === "es" ? "Confirmar y enviar email" : "Confirm + send email"}</>}
          cancelLabel={lang === "es" ? "Cancelar" : "Cancel"}
          onConfirm={() => handleConfirmValidate(true)}
          onCancel={() => setActionStep(null)}>
          <button type="button" onClick={() => handleConfirmValidate(false)}
            style={{ ...btn.ghost, ...(isMobile ? { flex: "1 1 100%", minHeight: 44 } : btnSm) }}>
            {lang === "es" ? "Confirmar sin email" : "Confirm without email"}
          </button>
        </ConfirmBar>
      )}
      {actionStep === "confirmReject" && (
        <ConfirmBar style={{ marginBottom: 16 }}
          message={lang === "es" ? <>¿Rechazar a <strong>{candidate.name}</strong>?</> : <>Reject <strong>{candidate.name}</strong>?</>}
          actionLabel={lang === "es" ? "Rechazar" : "Reject"}
          cancelLabel={lang === "es" ? "Cancelar" : "Cancel"}
          onConfirm={async () => {
            const before = candidateStatuses[candidate.id];
            setCandidateStatuses(prev => ({ ...prev, [candidate.id]: "rejected" }));
            setActionStep(null);
            if (!(await saveCandidate({ status: "rejected" }))) { setCandidateStatuses(prev => ({ ...prev, [candidate.id]: before })); return; }
            track("candidate_validated", { decision: "rejected", position: position?.title || "" });
            supabase.from("admin_notifications").insert({ type: "candidate_rejected", message: `${candidate.name} rejected for ${position?.title || ""}`, candidate_name: candidate.name, position_title: position?.title || "" });
          }}
          onCancel={() => setActionStep(null)} />
      )}
      {actionStep === "scheduleInterview" && (
        <ConfirmBar danger={false} style={{ marginBottom: 16 }}
          message={lang === "es" ? <>Enviar link de Calendly a <strong>{candidateEmail || candidate.name}</strong></> : <>Send Calendly link to <strong>{candidateEmail || candidate.name}</strong></>}
          actionLabel={<>✉ {lang === "es" ? "Enviar" : "Send"}</>}
          cancelLabel={lang === "es" ? "Cancelar" : "Cancel"}
          onConfirm={handleScheduleInterview}
          onCancel={() => setActionStep(null)} />
      )}

      {/* Dónde empiezan las pestañas: a este punto se vuelve al cambiar de pestaña */}
      <div ref={tabsAnchorRef} />
      {/* Pestañas: mismo conmutador que las de la posición. En móvil se quedan
          fijas bajo la barra superior al bajar (24/10) */}
      <div style={isMobile ? { position: "sticky", top: `calc(${MOBILE_TOP}px + env(safe-area-inset-top))`, zIndex: 5, background: SURFACE, margin: "0 -16px 12px", padding: "8px 16px" } : undefined}>
      <div style={{ ...segWrap, display: isMobile ? "flex" : "inline-flex", marginBottom: isMobile ? 0 : 24, ...(isMobile ? { width: "100%", boxSizing: "border-box", overflowX: "auto" } : null) }}>
        {tabs.map(tab_ => {
          const active = activeTab === tab_.id;
          return (
            <button key={tab_.id} onClick={() => handleSetTab(tab_.id)}
              style={{ ...segBtn(active), padding: "0 20px", height: isMobile ? 40 : 34, transition: "all 0.12s", ...(isMobile ? { flex: "1 1 auto", padding: "0 8px", fontSize: 12 } : null), ...emptyTab(tab_) }}>{/* móvil: las cuatro caben sin desplazar (31/10) */}
              {tab_.label}
            </button>
          );
        })}
      </div>
      </div>

      {/* Tab content */}
      {tab === "report" && (() => {
        // ── INFORME, mismo esquema que Evaluación: resumen fijo a la izquierda
        // y el detalle a la derecha (Cualificación · Referencias · CV) en
        // tablas limpias. Sin cajas dentro de cajas ni acordeones cerrados.
        const es = lang === "es";
        const card = { background: WHITE, border: "1.5px solid " + LINE, borderRadius: 14 };
        const lbl = text.label;
        const lnk = { ...linkBtn, textAlign: "left", ...(isMobile ? { minHeight: 40, fontSize: 13 } : null) };
        const refsData = candidate.refsData || [];
        // Resumen a la izquierda: en Cualificación siempre; en Referencias, con alguna (03/11)
        const showSummary = reportSub === "qual" || (reportSub === "refs" && refsData.length > 0);
        const sections = candidate.qualSections || [];
        const vectors = [
          [t.technicalFit, candidate.scoring.technical],
          [t.softSkills, candidate.scoring.soft],
          [t.culturalFit, candidate.scoring.cultural],
          [t.growthPotential, candidate.scoring.growth],
        ];
        const genRefInsights = async () => {
          setLiveRefInsightsLoading(true);
          try {
            const refText = refsData.map(refView).map((r, i) => "Reference " + (i + 1) + ": " + r.title + "\n" + r.items.map(([k, v]) => k + ": " + v).join("\n")).join("\n\n");
            const res = await aiFetch({ body: JSON.stringify({ max_tokens: 600,
              system: `You are a senior executive recruiter analysing reference data. Return ONLY a JSON object with: "pct" (string like "82%" — estimated alignment between references), "points" (array of 3-5 strings, key coinciding points), "verdict" (1-2 sentence overall recommendation). ${es ? "Write points and verdict in Spanish." : "Write points and verdict in English."} No markdown.`,
              messages: [{ role: "user", content: "Candidate: " + candidate.name + "\n\nReferences:\n" + refText }] }) });
            const data = await res.json();
            setLiveRefInsights(JSON.parse((data.content?.[0]?.text || "{}").replace(/```json|```/g, "").trim()));
          } catch (e) { console.error(e); }
          setLiveRefInsightsLoading(false);
        };

        return (
          <div style={{ display: "grid", gridTemplateColumns: isMobile || !showSummary ? "minmax(0, 1fr)" : "minmax(280px, 330px) minmax(0, 1fr)", gap: 16, alignItems: "start" }}>

            {/* ── Resumen (fijo en escritorio; en móvil, arriba y sin fijar).
                03/11: solo en Cualificación, que es de donde sale ── */}
            {reportSub === "qual" && (
              <SummaryPanel es={es} isMobile={isMobile} vectors={vectors} strengths={displayStrengths} weaknesses={displayWeaknesses} strengthsLabel={t.strengths}
                footer={isAdmin && (<>
                  <button onClick={generateReportInsights} disabled={reportInsightsLoading} style={{ ...lnk, cursor: reportInsightsLoading ? "wait" : "pointer" }}>
                    {reportInsightsLoading ? (es ? "Analizando..." : "Analysing...") : (es ? "↻ Recalcular con IA" : "↻ Recalculate with AI")}
                  </button>
                  <button onClick={handleSendQualForm} disabled={qualLinkLoading} style={lnk}>
                    {qualLinkCopied ? (es ? "✓ Enlace copiado" : "✓ Link copied") : qualLinkLoading ? (es ? "Generando..." : "Generating...") : (es ? "⎘ Copiar enlace del cuestionario" : "⎘ Copy questionnaire link")}
                  </button>
                </>)} />
            )}

            {reportSub === "refs" && showSummary && (
              <SummaryPanel es={es} isMobile={isMobile}
                sub={`${refsData.length} ${es ? (refsData.length === 1 ? "referencia" : "referencias") : (refsData.length === 1 ? "reference" : "references")}`}
                vectors={liveRefInsights ? [[es ? "Coincidencia entre referencias" : "Agreement between references", Number(refOutOf5(liveRefInsights.pct)) || 0]] : []}
                strengths={liveRefInsights ? refPointsL.slice(0, -1).filter(Boolean) : []} strengthsLabel={t.coincidingPointsTitle}
                extra={liveRefInsights?.verdict && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ ...text.label, marginBottom: 8 }}>{es ? "Conclusión" : "Verdict"}</div>
                    <div style={{ fontSize: 13, color: INK, lineHeight: 1.55 }}>{refPointsL[refPointsL.length - 1]}</div>
                  </div>
                )}
                footer={refsData.length >= 2 ? (
                  <button onClick={genRefInsights} disabled={liveRefInsightsLoading} style={{ ...lnk, cursor: liveRefInsightsLoading ? "wait" : "pointer" }}>
                    {liveRefInsightsLoading ? (es ? "Analizando..." : "Analysing...") : liveRefInsights ? (es ? "↻ Regenerar con IA" : "↻ Regenerate with AI") : (es ? "✦ Analizar referencias" : "✦ Analyse references")}
                  </button>
                ) : <span style={{ fontSize: 12, color: MUTED }}>{t.willAppearSecondRef}</span>} />
            )}

            {/* ── Detalle ── */}
            <div style={{ minWidth: 0 }}>

              {reportSub === "qual" && (
                <div style={{ ...card, overflow: "hidden" }}>
                  {sections.length === 0 ? (
                    <div style={{ ...text.body, color: MUTED, padding: "28px 24px" }}>{es ? "El candidato todavía no ha respondido al cuestionario." : "The candidate hasn't answered the questionnaire yet."}</div>
                  ) : sections.map((section, si) => (
                    <div key={si}>
                      <div style={{ ...lbl, background: SURFACE, padding: isMobile ? "10px 16px" : "10px 20px", borderTop: si ? `1px solid ${LINE}` : "none", borderBottom: `1px solid ${LINE}` }}>{qualL(si)}</div>
                      {(section.items || []).map((item, ii) => {
                        const editingQ = isAdmin && editingQuestion && editingQuestion.si === si && editingQuestion.ii === ii;
                        return (
                          <div key={ii} style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "minmax(180px, 34%) 1fr", gap: isMobile ? 6 : 20, padding: isMobile ? "12px 16px" : "12px 20px", borderBottom: `1px solid ${ROW_LINE}` }}>
                            <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.55, display: "flex", gap: 6, alignItems: "flex-start" }}>
                              <span style={{ color: FAINT, fontWeight: 700, minWidth: 22 }}>{item.n}</span>
                              {editingQ ? (
                                <input autoFocus defaultValue={item.q}
                                  onBlur={async (e) => {
                                    const nv = e.target.value.trim();
                                    if (nv && nv !== item.q) {
                                      const ns = sections.map((s2, si2) => si2 !== si ? s2 : { ...s2, items: s2.items.map((it, ii2) => ii2 !== ii ? it : { ...it, q: nv }) });
                                      if (await saveCandidate({ qual_sections: ns })) onUpdateCandidate({ ...candidate, qualSections: ns });
                                    }
                                    setEditingQuestion(null);
                                  }}
                                  onKeyDown={e => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") setEditingQuestion(null); }}
                                  style={{ flex: 1, border: `1.5px solid ${BRAND_LIGHT}`, borderRadius: 8, padding: "2px 6px", fontSize: 12, color: INK, fontFamily: "inherit", outline: "none", ...(isMobile ? { fontSize: 16, minHeight: 44, minWidth: 0, boxSizing: "border-box" } : null) }} />
                              ) : (
                                <span style={{ flex: 1, cursor: isAdmin ? "text" : "default" }} title={isAdmin ? (es ? "Clic para editar la pregunta" : "Click to edit the question") : ""}
                                  onClick={() => isAdmin && setEditingQuestion({ si, ii })}>{qualL(si, ii)}</span>
                              )}
                            </div>
                            {isAdmin && isMobile && !(editingAnswer && editingAnswer.si === si && editingAnswer.ii === ii) ? (
                              <div onClick={() => setEditingAnswer({ si, ii })} style={{ fontSize: 13, color: item.a ? INK : FAINT, lineHeight: 1.55, whiteSpace: "pre-wrap", cursor: "text" }}>{item.a || (es ? "Sin respuesta" : "No answer")}</div>
                            ) : isAdmin ? (
                              <textarea autoFocus={isMobile} defaultValue={item.a} rows={Math.max(1, Math.min(6, Math.ceil(String(item.a || "").length / 90)))}
                                onBlur={async (e) => {
                                  const nv = e.target.value;
                                  if (nv === (item.a || "")) return;
                                  const ns = sections.map((s2, si2) => si2 !== si ? s2 : { ...s2, items: s2.items.map((it, ii2) => ii2 !== ii ? it : { ...it, a: nv }) });
                                  if (await saveCandidate({ qual_sections: ns })) onUpdateCandidate({ ...candidate, qualSections: ns });
                                }}
                                placeholder={es ? "Sin respuesta" : "No answer"}
                                style={{ width: "100%", boxSizing: "border-box", border: "1.5px solid transparent", borderRadius: 8, background: "transparent", fontSize: isMobile ? 16 : 13, color: INK, lineHeight: 1.55, resize: "vertical", fontFamily: "inherit", outline: "none", padding: "0 4px", margin: "-1px -4px", ...(isMobile ? { width: "calc(100% + 8px)", minHeight: 44 } : null) }}
                                onFocus={e => { e.target.style.borderColor = BRAND_LIGHT; e.target.style.background = HOVER_BG; }}
                                onBlurCapture={e => { e.target.style.borderColor = "transparent"; e.target.style.background = "transparent"; if (isMobile) setEditingAnswer(null); }} />
                            ) : (
                              <div style={{ fontSize: 13, color: item.a ? INK : FAINT, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{item.a || (es ? "Sin respuesta" : "No answer")}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}

              {reportSub === "refs" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div style={{ ...card, padding: isMobile ? "16px" : "18px 20px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
                      <span style={text.cardTitle}>{t.references}</span>
                      {isAdmin && (
                        <ReferenceManager candidate={candidate} lang={lang} isAdmin={isAdmin}
                          onApproveReference={async (newRef) => {
                            const upd = { ...candidate, refsData: [...refsData, newRef], references: (candidate.references || 0) + 1 };
                            if (await saveCandidate({ refs_data: upd.refsData, references_count: upd.references })) onUpdateCandidate(upd);
                          }}
                          addButton={<AddReferenceInline candidate={candidate} lang={lang} onSave={async (nr) => { const upd = { ...candidate, refsData: [...refsData, nr], references: (candidate.references || 0) + 1 }; if (await saveCandidate({ refs_data: upd.refsData, references_count: upd.references })) onUpdateCandidate(upd); }} />} />
                      )}
                      {!isAdmin && currentStatus === "approved" && hasEval && !refRequested && (
                        <button onClick={handleRequestRefs} disabled={refRequesting}
                          style={{ ...btn.primary, ...btnSm, cursor: refRequesting ? "not-allowed" : "pointer", opacity: refRequesting ? 0.6 : 1, ...(isMobile ? { width: "100%", height: 44, fontSize: 13 } : null) }}>
                          {refRequesting ? (es ? "Enviando..." : "Sending...") : (es ? "Solicitar referencias" : "Request references")}
                        </button>
                      )}
                      {!isAdmin && refRequested && <span style={savedText}>✓ {es ? "Solicitud enviada" : "Request sent"}</span>}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {refsData.length > 0 ? refsData.map((ref, ri) => (
                        <RefAccordion key={ri} ref_={ref} lang={lang} onDelete={isAdmin ? async () => {
                          const updated = refsData.filter((_, i) => i !== ri);
                          const extraClean = updated.length === 0 ? { ref_pct: null, ref_insights: null } : {};
                          if (!(await saveCandidate({ refs_data: updated, references_count: updated.length, ...extraClean }))) return;
                          onUpdateCandidate({ ...candidate, refsData: updated, references: updated.length, ...(updated.length === 0 ? { refPct: null, refInsights: null } : {}) });
                        } : undefined} />
                      )) : (
                        <div style={{ ...text.body, color: MUTED, padding: "8px 0" }}>{t.noReferencesYet}</div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {reportSub === "cv" && <CVTab candidate={candidate} isAdmin={isAdmin} lang={lang} />}
            </div>
          </div>
        );
      })()}

      {/* ── EVALUACIÓN: evaluación arriba, comparar debajo, guía a la derecha */}
      {tab === "evaluation" && (() => {
        // 03/11: mismo esquema que Cualificación. Con alguna entrevista, el
        // resumen a la izquierda (vectores según las entrevistas y lo que
        // resume la IA); sin ninguna, la evaluación a todo el ancho.
        const es = lang === "es";
        const named = (liveManagers || []).filter(m => (liveScores || {})[m.id] && Object.values(liveScores[m.id]).some(v => v > 0));
        const showSum = !!candidateEval && named.length > 0;
        const lnk = { ...linkBtn, textAlign: "left", ...(isMobile ? { minHeight: 40, fontSize: 13 } : null) };
        return (
          <div style={{ display: "grid", gridTemplateColumns: isMobile || !showSum ? "minmax(0, 1fr)" : "minmax(280px, 330px) minmax(0, 1fr)", gap: 16, alignItems: "start" }}>
            {showSum && (
              <SummaryPanel es={es} isMobile={isMobile}
                sub={`${named.length} ${es ? (named.length === 1 ? "entrevista" : "entrevistas") : (named.length === 1 ? "interview" : "interviews")} · ${named.map(m => m.name).filter(Boolean).join(", ")}`}
                vectors={[[t.technicalFit, candidateEval.technical], [t.softSkills, candidateEval.soft], [t.culturalFit, candidateEval.cultural], [t.growthPotential, candidateEval.growth]]}
                strengths={evalStrengthsL || []} weaknesses={evalWeaknessesL || []} strengthsLabel={t.strengths}
                footer={(
                  <button onClick={generateEvalInsights} disabled={evalInsightsLoading} style={{ ...lnk, cursor: evalInsightsLoading ? "wait" : "pointer" }}>
                    {evalInsightsLoading ? (es ? "Analizando..." : "Analysing...")
                      : liveAiInsights?.strengths?.length ? (es ? "↻ Recalcular con IA" : "↻ Recalculate with AI")
                      : (es ? "✦ Resumir las entrevistas con IA" : "✦ Summarise the interviews with AI")}
                  </button>
                )} />
            )}
            <div style={{ minWidth: 0 }}>
              {/* Mismo componente y formato que la pestaña Evaluación de la posición,
                  solo para este candidato: una sola forma de evaluar en toda la app. */}
              <PositionEvaluationTab position={position} lang={lang} photoUrls={photoUrls} onlyCandidate={candidate}
                onEvalChange={(id, next) => { setLiveManagers(next.managers); setLiveScores(next.scores); setLiveNotes(next.notes || {}); }} />
            </div>
          </div>
        );
      })()}

      {/* ── COMPARAR: pestaña propia */}
      {tab === "compare" && (
      <div>
      {tab === "compare" && (() => {
        const evalA = candidateEval;
        const evalB = compareCandidate ? computeEvalFromScores(compareCandidate.evalScores || {}, compareCandidate.evalManagers || []) : null;
        // Una columna por persona: en el diseño a tres columnas cada candidato
        // va a un lado del gráfico, no los dos juntos debajo.
        // El nombre va en una fila propia por encima de las tres columnas, no
        // dentro de cada una: así las cajas arrancan todas a la misma altura.
        const QualColumn = ({ sw, wk, color }) => (
          <div>
            <div style={{ background: SURFACE, border: `1.5px solid ${LINE}`, borderRadius: 10, padding: "12px 14px", marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 10 }}>{t.strengths}</div>
              {(sw || []).slice(0, 4).map((x, i) => <div key={i} style={{ display: "flex", gap: 7, marginBottom: 7 }}><div style={{ width: 5, height: 5, borderRadius: "50%", background: color, marginTop: 6, flexShrink: 0 }} /><span style={{ fontSize: 12, color: INK, lineHeight: 1.6 }}>{x}</span></div>)}
              {!(sw || []).length && <div style={{ fontSize: 12, color: FAINT }}>—</div>}
            </div>
            <div style={{ background: SURFACE, border: `1.5px solid ${LINE}`, borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 10 }}>{t.weaknesses}</div>
              {(wk || []).slice(0, 4).map((x, i) => <div key={i} style={{ display: "flex", gap: 7, marginBottom: 7 }}><div style={{ width: 5, height: 5, borderRadius: "50%", background: color, marginTop: 6, flexShrink: 0 }} /><span style={{ fontSize: 12, color: INK, lineHeight: 1.6 }}>{x}</span></div>)}
              {!(wk || []).length && <div style={{ fontSize: 12, color: FAINT }}>—</div>}
            </div>
          </div>
        );

        const ScoreTable = ({ getA, getB, getEvalA, getEvalB, labelA, labelB }) => (
          <div style={{ flex: 1, border: `1.5px solid ${LINE}`, borderRadius: 12, overflow: "hidden", minWidth: 0 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: SURFACE, borderBottom: `1.5px solid ${LINE}` }}>
                  <th style={{ padding: "10px", textAlign: "left", fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4 }}>{lang === "es" ? "Dimensión" : "Dimension"}</th>
                  <th style={{ padding: "10px 8px", textAlign: "center", fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4 }}>{candidate.name.split(" ")[0]}</th>
                  {compareCandidate && !isAverage && <th style={{ padding: "10px 8px", textAlign: "center", fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4 }}>{compareCandidate.name.split(" ")[0]}</th>}
                  {isAverage && <th style={{ padding: "10px 8px", textAlign: "center", fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4 }}>{lang === "es" ? "Media" : "Avg"}</th>}
                </tr>
              </thead>
              <tbody>
                {[{ label: t.technicalFit, key: "technical" }, { label: t.softSkills, key: "soft" }, { label: t.culturalFit, key: "cultural" }, { label: t.growthPotential, key: "growth" }].map(({ label, key }, i) => {
                  const vA = getA(key); const vB = getB ? getB(key) : null;
                  const win = vB !== null ? (vA > vB ? "A" : vA < vB ? "B" : "tie") : null;
                  return (
                    <tr key={i} style={{ borderBottom: i < 3 ? `1px solid ${ROW_LINE}` : "none" }}>
                      <td style={{ padding: "10px", fontSize: 12, color: TEXT, fontWeight: 600 }}>{label}</td>
                      <td style={{ padding: "10px 8px", textAlign: "center" }}>
                        <span style={{ fontWeight: win === "A" ? 700 : 500, fontSize: 13, color: win === "A" ? INK : MUTED }}>{typeof vA === "number" ? vA % 1 === 0 ? vA : vA.toFixed(1) : "—"}</span>
                        <span style={{ fontSize: 11, color: FAINT }}>/5</span>
                      </td>
                      {(compareCandidate || isAverage) && (
                        <td style={{ padding: "10px 8px", textAlign: "center" }}>
                          <span style={{ fontWeight: win === "B" ? 700 : 500, fontSize: 13, color: win === "B" ? INK : MUTED }}>{vB !== null && vB !== undefined ? (typeof vB === "number" ? vB % 1 === 0 ? vB : vB.toFixed(1) : "—") : "—"}</span>
                          <span style={{ fontSize: 11, color: FAINT }}>/5</span>
                        </td>
                      )}
                    </tr>
                  );
                })}
                <tr style={{ background: SURFACE, borderTop: `1.5px solid ${LINE}` }}>
                  <td style={{ padding: "10px", fontSize: 12, fontWeight: 600, color: TEXT }}>{lang === "es" ? "Media" : "Average"}</td>
                  <td style={{ padding: "10px 8px", textAlign: "center" }}><span style={{ fontWeight: 700, fontSize: 13, color: MUTED }}>{getA ? parseFloat((["technical","soft","cultural","growth"].map(k=>getA(k)).reduce((a,b)=>a+b,0)/4).toFixed(1)) : "—"}</span><span style={{ fontSize: 11, color: FAINT }}>/5</span></td>
                  {(compareCandidate || isAverage) && <td style={{ padding: "10px 8px", textAlign: "center" }}><span style={{ fontWeight: 700, fontSize: 13, color: MUTED }}>{getB ? parseFloat((["technical","soft","cultural","growth"].map(k=>getB(k)).reduce((a,b)=>a+b,0)/4).toFixed(1)) : "—"}</span><span style={{ fontSize: 11, color: FAINT }}>/5</span></td>}
                </tr>
              </tbody>
            </table>
          </div>
        );

        return (
        <div style={{ ...cardTok, padding: isMobile ? 16 : 22, width: "100%", boxSizing: "border-box" }}>
          {/* Móvil (24/10): "Beatriz vs [Elegir…]" y UN conmutador con lo que se
              compara. Antes: título repetido, dos conmutadores (con "Evaluación"
              dos veces) y un desplegable enorme */}
          {/* Un solo selector «A vs [Elegir…]» para escritorio y móvil (31/10):
              antes el escritorio tenía otro, un desplegable del sistema */}
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, ...(isMobile ? null : { maxWidth: 460 }) }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: INK, flexShrink: 0 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: ARCH_COLORS[0] }} />{candidate.name.split(" ")[0]}</span>
              <span style={{ ...text.meta, fontSize: 12, flexShrink: 0 }}>vs</span>
              <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
                <div style={{ height: 40, border: `1.5px ${compareId ? "solid" : "dashed"} ${compareId ? LINE : LINE}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, padding: "0 12px", boxSizing: "border-box" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, fontSize: 13, fontWeight: compareId ? 700 : 400, color: compareId ? INK : MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {compareId && !isAverage && <span style={{ width: 8, height: 8, borderRadius: "50%", background: ARCH_COLORS[2], flexShrink: 0 }} />}
                    {compareId ? (isAverage ? t.candidateAverage : compareCandidate?.name) : (lang === "es" ? "Elegir…" : "Choose…")}
                  </span>
                  <ChevronDown size={16} strokeWidth={1.75} color={MUTED} />
                </div>
                {/* Móvil: el selector del teléfono. Escritorio: la lista común
                    (OptionList, 01/11), no la nativa de Windows con su letra */}
                {isMobile ? (
                  <select aria-label={t.compareWith} value={compareId} onChange={e => setCompareId(e.target.value)} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, fontSize: 16, cursor: "pointer" }}>
                    {compareOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                ) : (<>
                  <button type="button" aria-label={t.compareWith} aria-haspopup="listbox" aria-expanded={compareOpen} onClick={() => setCompareOpen(o => !o)}
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", background: "transparent", border: "none", cursor: "pointer", padding: 0 }} />
                  {compareOpen && <OptionList options={compareOptions} value={compareId} onPick={setCompareId} onClose={() => setCompareOpen(false)} align="left" offset={0} />}
                </>)}
              </div>
            </div>
            <div style={{ ...segWrap, display: isMobile ? "flex" : "inline-flex", ...(isMobile ? { width: "100%" } : null), boxSizing: "border-box", marginBottom: isMobile ? 12 : 20 }}>
              {[["qualification", lang === "es" ? "Cualificación" : "Qualification"], ["evaluation", lang === "es" ? "Entrevistas" : "Interviews"], ["salary", lang === "es" ? "Años vs. salario" : "Years vs. salary"]].map(([v, label]) => {
                const on = v === "salary" ? compareTab === "salary" : (compareTab !== "salary" && compareSource === v);
                return (
                  <button key={v} onClick={() => { if (v === "salary") setCompareTab("salary"); else { setCompareTab("qualification"); setCompareSource(v); } }}
                    style={isMobile ? { ...segBtn(on), flex: 1, height: 36, padding: "0 6px", fontSize: 12 } : segBtn(on)}>{label}</button>
                );
              })}
            </div>
          </>

          {/* ── EVALUACIÓN: tres columnas ──────────────────────────────
              Los nombres y el conmutador viven en una fila propia por encima
              de la rejilla, para que las tres columnas de contenido arranquen
              exactamente a la misma altura. */}
          {compareTab === "qualification" && (() => {
            const isEval = compareSource === "evaluation";
            const noEval = isEval && !evalA;

            const scA = isEval ? evalA : candidate.scoring;
            const scB = isEval ? evalB : (compareCandidate ? compareCandidate.scoring : (isAverage ? avgScoring : null));
            const swA = isEval ? evalStrengthsL : displayStrengths;
            const wkA = isEval ? evalWeaknessesL : displayWeaknesses;
            const swB = isEval ? cmpEvalStrengthsL : cmpStrengthsL;
            const wkB = isEval ? cmpEvalWeaknessesL : cmpWeaknessesL;
            // Las fortalezas del candidato abierto se ven SIEMPRE. Antes
            // dependían de haber elegido a alguien con quien comparar, así que
            // sin selección la pantalla solo mostraba el gráfico y toda la
            // información cualitativa se perdía.
            const showSides = compareCandidate && !isAverage;   // hay segunda columna
            const cols = showSides
              ? "minmax(0,1fr) minmax(440px, 1.6fr) minmax(0,1fr)"
              : "minmax(0,1fr) minmax(440px, 1.6fr)";
            // Móvil: una columna. Primero el gráfico y la tabla; debajo, cada
            // persona con su nombre encima de sus fortalezas y áreas a explorar.
            const radarSize = isMobile ? Math.min(300, Math.max(220, window.innerWidth - 32 - 32 - 24 - 4)) : 440;

            const nameTag = (name, color, align) => (
              <div style={{ display: "flex", alignItems: "center", gap: 7, justifyContent: align }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
                <span style={{ fontWeight: 700, fontSize: 13, color: INK }}>{name}</span>
              </div>
            );

            return (
              <div>
                {/* Fila de cabecera: nombres a los lados (el conmutador va arriba; en móvil
                    ya va arriba, junto al "vs") */}
                {!isMobile && <div style={{ display: "grid", gridTemplateColumns: cols, gap: 28, alignItems: "center", marginBottom: 14 }}>
                  {!isMobile && nameTag(candidate.name, ARCH_COLORS[0], "flex-start")}
                  <div />
                  {!isMobile && showSides && nameTag(compareCandidate.name, ARCH_COLORS[2], "flex-end")}
                </div>}

                {noEval ? (
                  <div style={{ background: SURFACE, border: "1.5px dashed " + LINE, borderRadius: 12, padding: "40px 24px", textAlign: "center" }}>
                    <div style={{ ...text.body, color: MUTED, marginBottom: 4 }}>
                      {lang === "es" ? "Sin evaluación de empresa para este candidato." : "No company evaluation for this candidate."}
                    </div>
                    <button onClick={() => handleSetTab("evaluation")} style={{ ...linkBtn, ...(isMobile ? { minHeight: 40, fontSize: 13 } : null) }}>
                      {lang === "es" ? "Complétala en la pestaña Evaluación" : "Complete it in the Evaluation tab"}
                    </button>
                  </div>
                ) : (
                  <div style={isMobile ? { display: "flex", flexDirection: "column", gap: 20 } : { display: "grid", gridTemplateColumns: cols, gap: 28, alignItems: "start" }}>
                    {!isMobile && QualColumn({ sw: swA, wk: wkA, color: ARCH_COLORS[0] })}

                    <div style={isMobile ? { minWidth: 0 } : undefined}>
                      <div style={{ background: SURFACE, borderRadius: 12, padding: 12, border: `1.5px solid ${LINE}`, display: "flex", justifyContent: "center", ...(isMobile ? { overflow: "hidden" } : null) }}>
                        <RadarChart
                          candidateA={{ scoring: scA }}
                          candidateB={scB ? { scoring: scB } : null}
                          labels={radarLabels} tooltips={radarTooltips}
                          size={radarSize}
                        />
                      </div>
                      <div style={{ marginTop: 14 }}>
                        {ScoreTable({ getA: k => scA?.[k], getB: scB ? (k => scB[k]) : null })}
                      </div>
                    </div>

                    {isMobile && (
                      <div>
                        <div style={{ marginBottom: 8 }}>{nameTag(candidate.name, ARCH_COLORS[0], "flex-start")}</div>
                        {QualColumn({ sw: swA, wk: wkA, color: ARCH_COLORS[0] })}
                      </div>
                    )}
                    {isMobile && showSides && (
                      <div>
                        <div style={{ marginBottom: 8 }}>{nameTag(compareCandidate.name, ARCH_COLORS[2], "flex-start")}</div>
                        {QualColumn({ sw: swB, wk: wkB, color: ARCH_COLORS[2] })}
                      </div>
                    )}
                    {!isMobile && showSides && QualColumn({ sw: swB, wk: wkB, color: ARCH_COLORS[2] })}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── SALARY MAP SUB-TAB ── */}
          {/* ── AÑOS VS. SALARIO (31/10) ─────────────────────────────────
              Sustituye a «Salarios», que pintaba a todos en fila e ignoraba a
              quién se elegía en «vs». Un punto por candidato de la posición:
              X = años de experiencia, Y = expectativa salarial (el número más
              alto de «Salario»). El abierto y el elegido, con color y nombre;
              el resto, en gris. Con «Media», un aro en la media de los demás. */}
          {compareTab === "salary" && (() => {
            const es = lang === "es";
            const parseSalary = (s) => {
              if (!s) return null;
              // «180.000€ / 210.000€»: fuera separadores de miles, se queda el mayor
              const cleaned = String(s).replace(/\.(?=\d{3})/g, "").replace(/,(?=\d{3})/g, "");
              const nums = (cleaned.match(/\d+/g) || []).map(Number).filter(n => n > 10000);
              return nums.length ? Math.max(...nums) : null;
            };
            const fmtK = (n) => `${Math.round(n / 1000)}k`;
            const pointOf = (c) => {
              const years = c?.yearsExperience;
              const salary = parseSalary(c?.salary);
              return years != null && salary ? { id: c.id, name: c.name, years: Number(years), salary } : null;
            };
            const all = allCandidates.map(pointOf).filter(Boolean);
            const missing = allCandidates.length - all.length;
            const pA = pointOf(candidate);
            const others = all.filter(d => String(d.id) !== String(candidate.id));
            const pB = isAverage
              ? (others.length ? { id: "average", name: es ? "Media" : "Average", years: others.reduce((s, d) => s + d.years, 0) / others.length, salary: others.reduce((s, d) => s + d.salary, 0) / others.length } : null)
              : (compareCandidate ? pointOf(compareCandidate) : null);
            const plotted = [...all, ...(pB && isAverage ? [pB] : [])];

            const statBox = (who, p, color, raw) => (
              <div style={{ flex: 1, minWidth: 0, border: `1.5px solid ${LINE}`, borderRadius: 12, padding: "12px 14px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />{who}
                </div>
                {p ? (
                  <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
                    <div><div style={{ fontSize: 22, fontWeight: 800, color: INK }}>{Math.round(p.years)}</div><div style={text.meta}>{es ? "años" : "years"}</div></div>
                    <div><div style={{ fontSize: 22, fontWeight: 800, color: INK }}>{fmtK(p.salary)}</div><div style={text.meta}>{es ? "expectativa" : "expectation"}</div></div>
                  </div>
                ) : (
                  <div style={{ ...text.meta, marginTop: 8 }}>
                    {raw?.salary ? (es ? "Sin años de experiencia" : "No years of experience") : (es ? "Sin años ni salario" : "No years or salary")}
                  </div>
                )}
              </div>
            );

            if (all.length === 0) return (
              <div style={{ ...text.empty, padding: "24px 0", textAlign: "center" }}>
                {es ? "Ningún candidato de la posición tiene a la vez años de experiencia y salario." : "No candidate in this position has both years of experience and salary."}
              </div>
            );

            const chartH = isMobile ? 240 : 300;
            const chartW = isMobile ? Math.max(260, Math.min(560, window.innerWidth - 32 - 32 - 2)) : 600;
            const mL = 44, mB = 34, mT = 18, mR = 16;
            const innerW = chartW - mL - mR, innerH = chartH - mB - mT;
            const maxYears = Math.max(...plotted.map(d => d.years));
            const xMax = Math.max(5, Math.ceil((maxYears + 1) / 5) * 5);
            const minS = Math.min(...plotted.map(d => d.salary)), maxS = Math.max(...plotted.map(d => d.salary));
            const step = 20000;
            const yMin = Math.max(0, Math.floor((minS - 5000) / step) * step);
            const yMax = Math.ceil((maxS + 5000) / step) * step;
            const toX = (v) => mL + (v / xMax) * innerW;
            const toY = (v) => mT + innerH - ((v - yMin) / ((yMax - yMin) || 1)) * innerH;
            const xTicks = Array.from({ length: xMax / 5 + 1 }, (_, i) => i * 5);
            const yTicks = Array.from({ length: Math.round((yMax - yMin) / step) + 1 }, (_, i) => yMin + i * step);
            const label = (p) => {
              const x = toX(p.years), y = toY(p.salary);
              const right = x < mL + innerW * 0.7;
              return (
                <text x={right ? x + 11 : x - 11} y={y + 4} textAnchor={right ? "start" : "end"} fontSize="12" fontWeight="800" fill={INK} fontFamily="DM Sans, sans-serif">
                  {p.id === "average" ? p.name : p.name.split(" ")[0]}
                </text>
              );
            };

            return (
              <div style={{ padding: "4px 0" }}>
                <div style={{ display: "flex", gap: 10, marginBottom: 18, maxWidth: 600 }}>
                  {statBox(candidate.name.split(" ")[0], pA, ARCH_COLORS[0], candidate)}
                  {compareId && statBox(isAverage ? (es ? "Media" : "Average") : (compareCandidate?.name || "").split(" ")[0], pB, ARCH_COLORS[2], isAverage ? { salary: true } : compareCandidate)}
                </div>
                <svg width={chartW} height={chartH} style={{ overflow: "visible", display: "block" }}>
                  {yTicks.map(v => (
                    <g key={"y" + v}>
                      <line x1={mL} x2={mL + innerW} y1={toY(v)} y2={toY(v)} stroke={NEUTRAL_BG} strokeWidth="1" />
                      <text x={mL - 6} y={toY(v) + 4} textAnchor="end" fontSize="11" fill={MUTED} fontFamily="DM Sans, sans-serif">{fmtK(v)}</text>
                    </g>
                  ))}
                  {xTicks.map(v => (
                    <text key={"x" + v} x={toX(v)} y={mT + innerH + 16} textAnchor="middle" fontSize="11" fill={MUTED} fontFamily="DM Sans, sans-serif">{v}</text>
                  ))}
                  <line x1={mL} x2={mL + innerW} y1={mT + innerH} y2={mT + innerH} stroke={LINE} strokeWidth="1" />
                  <text x={mL + innerW} y={chartH} textAnchor="end" fontSize="11" fill={MUTED} fontFamily="DM Sans, sans-serif">{es ? "Años de experiencia →" : "Years of experience →"}</text>
                  <text x={mL - 6} y={mT - 8} textAnchor="end" fontSize="11" fill={MUTED} fontFamily="DM Sans, sans-serif">€</text>
                  {others.filter(d => !pB || String(d.id) !== String(pB.id)).map(d => (
                    <circle key={d.id} cx={toX(d.years)} cy={toY(d.salary)} r={5} fill={FAINT}>
                      <title>{`${d.name} · ${d.years} ${es ? "años" : "yrs"} · ${fmtK(d.salary)}`}</title>
                    </circle>
                  ))}
                  {pB && (isAverage
                    ? <circle cx={toX(pB.years)} cy={toY(pB.salary)} r={7} fill={WHITE} stroke={ARCH_COLORS[2]} strokeWidth="2.5" />
                    : <circle cx={toX(pB.years)} cy={toY(pB.salary)} r={7} fill={ARCH_COLORS[2]} />)}
                  {pB && label(pB)}
                  {pA && <circle cx={toX(pA.years)} cy={toY(pA.salary)} r={10} fill="none" stroke={ARCH_QUAD_FILLS[0]} strokeWidth="2" />}
                  {pA && <circle cx={toX(pA.years)} cy={toY(pA.salary)} r={7} fill={ARCH_COLORS[0]} />}
                  {pA && label(pA)}
                </svg>
                {missing > 0 && (
                  <div style={{ ...text.meta, marginTop: 12 }}>
                    {es ? `${missing} ${missing === 1 ? "candidato no sale" : "candidatos no salen"}: les faltan años o salario.` : `${missing} ${missing === 1 ? "candidate isn't" : "candidates aren't"} shown: missing years or salary.`}
                  </div>
                )}
              </div>
            );
          })()}

        </div>
        );
      })()}
      </div>
      )}



    </div>
  );
};

export default CandidateDetailView;
